<template>
  <v-form>
    <v-row justify="center">
      <v-col cols="12" sm="9" xl="4">
        <div class="cropper-wrapper">
          <PhotoCropper
            ref="cropper"
            :imageUrl="imageUrl"
            :imageLoader="imageLoader"
            header-tooltip="message.posts.addImageTooltip"
            @upload-image="uploadImage"
            @delete="handleDeleteImage"
          />
        </div>

        <v-text-field
          name="title"
          v-model="form.event.title"
          counter="58"
          v-validate="'required|max:58'"
          :data-vv-name="$t('message.posts.eventName')"
          :error-messages="errors.collect($t('message.posts.eventName'))"
          :label="`${$t('message.posts.eventName')} *`"
          required
        ></v-text-field>

        <v-col cols="5" offset-lg="8" class="pt-0 pb-0">
          <v-switch
            inset
            color="primary"
            v-model="addTime"
            class="mt-0 -mt-4"
            hide-details
            :label="$t('message.posts.addTime')"
          ></v-switch>
        </v-col>

        <v-row>
          <v-col :cols="addTime ? 7 : 12">
            <v-menu
              ref="startDateMenu"
              v-model="menu.startDate"
              :close-on-content-click="false"
              :return-value.sync="form.event.schedule.start_date"
              transition="scale-transition"
              offset-y
              max-width="290px"
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="form.event.schedule.start_date"
                  v-validate="'required'"
                  :data-vv-name="$t('message.posts.startDate')"
                  :error-messages="
                    errors.collect($t('message.posts.startDate'))
                  "
                  :label="`${$t('message.posts.startDate')} *`"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="form.event.schedule.start_date"
                :locale="selectedLocale"
                type="date"
                no-title
                scrollable
                color="primary"
                eventColor="primary"
              >
                <v-spacer></v-spacer>
                <v-btn text color="primary" @click="menu.startDate = false">{{
                  $t("message.common.cancel")
                }}</v-btn>
                <v-btn
                  text
                  color="primary"
                  @click="
                    $refs.startDateMenu.save(form.event.schedule.start_date)
                  "
                  >{{ $t("message.common.ok") }}</v-btn
                >
              </v-date-picker>
            </v-menu>
            <v-menu
              ref="endDateMenu"
              v-model="menu.endDate"
              :close-on-content-click="false"
              :return-value.sync="form.event.schedule.end_date"
              transition="scale-transition"
              offset-y
              max-width="290px"
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="form.event.schedule.end_date"
                  v-validate="'required'"
                  :data-vv-name="$t('message.posts.endDate')"
                  :error-messages="errors.collect($t('message.posts.endDate'))"
                  :label="`${$t('message.posts.endDate')} *`"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  required
                  :disabled="!form.event.schedule.start_date"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="form.event.schedule.end_date"
                :locale="selectedLocale"
                type="date"
                :min="form.event.schedule.start_date"
                no-title
                scrollable
                color="primary"
                eventColor="primary"
              >
                <v-spacer></v-spacer>
                <v-btn text color="primary" @click="menu.endDate = false">{{
                  $t("message.common.cancel")
                }}</v-btn>
                <v-btn
                  text
                  color="primary"
                  @click="$refs.endDateMenu.save(form.event.schedule.end_date)"
                  >{{ $t("message.common.ok") }}</v-btn
                >
              </v-date-picker>
            </v-menu>
          </v-col>
          <v-col cols="5" v-if="addTime">
            <v-menu
              ref="startTimeMenu"
              v-model="menu.startTime"
              :close-on-content-click="false"
              :nudge-right="40"
              :return-value.sync="form.event.schedule.start_time"
              transition="scale-transition"
              offset-y
              max-width="290px"
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="form.event.schedule.start_time"
                  name="startTime"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  v-validate="{ required: addTime }"
                  :data-vv-name="$t('message.posts.startTime')"
                  :error-messages="
                    errors.collect($t('message.posts.startTime'))
                  "
                  :label="`${$t('message.posts.startTime')} *`"
                  required
                ></v-text-field>
              </template>
              <v-time-picker
                v-if="menu.startTime"
                format="24hr"
                color="primary"
                v-model="form.event.schedule.start_time"
                full-width
                @click:minute="
                  $refs.startTimeMenu.save(form.event.schedule.start_time)
                "
              ></v-time-picker>
            </v-menu>

            <v-menu
              ref="endTimeMenu"
              v-model="menu.endTime"
              :close-on-content-click="false"
              :nudge-right="40"
              :return-value.sync="form.event.schedule.end_time"
              transition="scale-transition"
              offset-y
              max-width="290px"
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="form.event.schedule.end_time"
                  name="endTime"
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  v-validate="'required'"
                  :data-vv-name="$t('message.posts.endTime')"
                  :error-messages="errors.collect($t('message.posts.endTime'))"
                  :label="`${$t('message.posts.endTime')} *`"
                  required
                  :disabled="!form.event.schedule.start_time"
                ></v-text-field>
              </template>
              <v-time-picker
                v-if="menu.endTime"
                format="24hr"
                color="primary"
                v-model="form.event.schedule.end_time"
                full-width
                :min="
                  form.event.schedule.start_date ===
                  form.event.schedule.end_date
                    ? form.event.schedule.start_time
                    : null
                "
                @click:minute="
                  $refs.endTimeMenu.save(form.event.schedule.end_time)
                "
              ></v-time-picker>
            </v-menu>
          </v-col>
        </v-row>
        <v-checkbox
          v-model="showExtra"
          :label="$t('message.posts.showExtra')"
        ></v-checkbox>

        <template v-if="showExtra">
          <v-textarea
            name="text"
            v-model="form.text"
            v-validate="'required'"
            :data-vv-name="$t('message.posts.schedule')"
            :error-messages="errors.collect($t('message.posts.schedule'))"
            :label="`${$t('message.posts.schedule')} *`"
            required
            rows="2"
            auto-grow
          ></v-textarea>

          <v-select
            :label="$t('message.posts.callToAction.label')"
            :items="[
              { name: $t('message.common.none'), value: 0 },
              ...callToActionItems
            ]"
            v-model="form.call_to_action.type"
            item-text="name"
            item-value="value"
          />

          <v-text-field
            v-if="form.call_to_action.type && form.call_to_action.type !== 6"
            name="url"
            v-model="form.call_to_action.url"
            v-validate="'required|url'"
            :data-vv-name="$t('message.posts.postUrl')"
            :error-messages="errors.collect($t('message.posts.postUrl'))"
            :label="`${$t('message.posts.postUrl')} *`"
            required
          ></v-text-field>
        </template>
      </v-col>
    </v-row>
    <v-row justify="center">
      <v-col cols="12" sm="9" xl="4">
        <PublicationDatePicker
          v-if="canPlanMarketingPublications"
          :type="type"
          :options="PUBLICATION_DATE_OPTIONS"
          :title="$t('message.posts.planning.publication.publicationDateTitle')"
          :subtitle="
            $t('message.posts.planning.publication.publicationDateSubtitle')
          "
          :edit-mode="editMode"
          :publish-at="form.publish_at"
          @change="handlePublicationDateChange"
        />
      </v-col>
    </v-row>
    <v-row justify="center" class="mt-4" no-gutters>
      <Footer>
        <ui-button
          color="primary"
          class="mr-8"
          @click="submit"
          :disabled="btnsDisabled"
        >
          {{ buttonLabel }}
        </ui-button>
        <ui-button
          outlined
          color="primary"
          icon="icon-search"
          :disabled="btnsDisabled"
          @click="showPreview()"
        >
          {{ $t("message.posts.preview") }}
        </ui-button>
      </Footer>
    </v-row>
  </v-form>
</template>

<script>
import { debounce } from "@/helpers/helpers";
import { mapState } from "vuex";
import cloneDeep from "lodash/cloneDeep";
import moment from "moment";

import PhotoCropper from "@/components/Photos/Cropper.vue";
import { POSTS_CALL_TO_ACTION_TYPE } from "@/enums/posts.enum";
import { postFormMixin } from "@/helpers/mixins/post-form";

export default {
  name: "PostsEventForm",

  model: {
    event: "setForm"
  },

  mixins: [postFormMixin],

  props: {
    imageLoader: {
      type: Boolean,
      required: true
    },
    imageUrl: {
      type: String,
      required: false
    },
    value: { type: Object },
    editMode: {
      default: false,
      type: Boolean
    }
  },

  data() {
    return {
      addTime: false,
      showExtra: false,
      menu: {
        startDate: false,
        endDate: false,
        startTime: false,
        endTime: false
      },
      items: Object.values(POSTS_CALL_TO_ACTION_TYPE),
      form: null
    };
  },

  components: { PhotoCropper },

  computed: {
    ...mapState({
      selectedLocale: state => state.settings.selectedLocale
    }),
    callToActionItems() {
      return this.items.map((item, index) => {
        return {
          name: this.$t(`message.posts.callToAction.${item}`),
          value: index + 1
        };
      });
    },
    btnsDisabled() {
      const editMode = this.$refs.cropper ? this.$refs.cropper.editMode : false;
      return this.imageLoader || editMode;
    }
  },

  created() {
    this.resetForm();
  },

  watch: {
    form: {
      handler: debounce(function(val) {
        this.setForm(val);
      }, 500),
      deep: true
    },
    "form.event.schedule.start_date": function() {
      if (this.value.event) return;
      this.form.event.schedule.end_date = null;
    },
    "form.event.schedule.start_time": function() {
      if (this.value.event) return;

      this.form.event.schedule.end_time = null;
    },
    "form.call_to_action.type": function(val) {
      if (!val || val === 6) {
        this.form.call_to_action.url = null;
      }
    },
    showExtra: function(val) {
      if (val === false) {
        this.form.text = null;
        this.form.call_to_action = {
          type: null,
          url: null
        };
      }
    }
  },
  methods: {
    setForm(val) {
      let formData = cloneDeep(val);
      if (
        formData.text ||
        (formData.call_to_action && formData.call_to_action.type)
      ) {
        this.showExtra = true;
      }
      this.setDateAndTime(formData);

      this.$emit("setForm", formData);
    },

    resetForm() {
      this.form = this.value.id
        ? this.setFormPreset(this.value)
        : {
            text: null,
            call_to_action: {
              type: null,
              url: null
            },
            event: {
              title: null,
              schedule: {
                start_date: null,
                start_time: null,
                end_date: null,
                end_time: null
              }
            },
            publish_at: null
          };
    }
  }
};
</script>

<style lang="scss" scoped>
.-mt-4 {
  margin-left: -15px;
}

.upload {
  position: relative;

  width: 100%;
  padding-top: 75%;
  margin-bottom: $spacing * 6;

  &__image-label {
    cursor: pointer;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    border: 3px dashed $primary;
    position: absolute;
    top: 0;
    left: 0;
    color: $primary;
    width: 100%;
    height: 100%;
    border-radius: 5px;
    font-weight: bold;
    background-size: cover;
    background-position: center center;

    i {
      @include font-size(58px);
      margin-bottom: $spacing * 1.25;
    }
  }
}

.info-icon {
  position: absolute;
  top: $spacing * -4;
  right: 0;
}

.cropper-wrapper {
  width: 100%;
  min-height: 250px;
  display: flex;
}
</style>
